// ABOUTME: Textarea component with color states and helper text
// ABOUTME: Provides consistent textarea styling across the application

import * as React from "react"
import { Controller, type Control, type FieldValues, type Path, type RegisterOptions } from "react-hook-form"
import { cn } from "@shared/lib/utils"
import { FIELD_SIZING_STYLE, resizeTextArea } from "@shared/hooks/useAutoSizeTextArea"

// Base Textarea component
const TextareaBase = React.forwardRef<HTMLTextAreaElement, React.ComponentProps<"textarea">>(
  ({ className, ...props }, ref) => {
    return (
      <textarea
        className={cn(
          "flex min-h-[60px] w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        ref={ref}
        {...props}
      />
    )
  },
)
TextareaBase.displayName = "TextareaBase"

interface CTextareaBaseProps extends Omit<React.ComponentPropsWithoutRef<"textarea">, "name"> {
  color?: "default" | "success" | "failure"
  shadow?: boolean
  helperText?: React.ReactNode
  /** Grow with the text instead of scrolling inside a fixed box. Pair it with
   *  min-h-* / max-h-* classes: the field keeps a starting size and a ceiling. */
  autoSize?: boolean
}

// Props when using uncontrolled pattern (with spread props)
interface CTextareaUncontrolledProps extends CTextareaBaseProps {
  control?: never
  rules?: never
  name?: string
}

// Props when using control pattern
interface CTextareaControlProps<T extends FieldValues> extends CTextareaBaseProps {
  control: Control<T>
  name: Path<T>
  rules?: RegisterOptions<T, Path<T>>
}

export type CTextareaProps<T extends FieldValues = FieldValues> = CTextareaUncontrolledProps | CTextareaControlProps<T>

const colorClasses = {
  default: "",
  success: "border-green-500 focus-visible:ring-green-500",
  failure: "border-red-500 focus-visible:ring-red-500",
}

// Inner component for the actual textarea rendering
const CTextareaInner = React.forwardRef<HTMLTextAreaElement, CTextareaBaseProps & { name?: string }>(
  ({ className, color, shadow, helperText, autoSize, style, onChange, ...props }, ref) => {
    const autoSizeRef = React.useRef<HTMLTextAreaElement | null>(null)

    // Typing is handled in onChange; this covers text that arrives from outside
    // — a Controller re-rendering with a new value, or a form reset writing
    // straight to the DOM. Keyed on the value rather than on every render: the
    // JS path (Firefox) reads scrollHeight and writes height, and a page of ten
    // such fields would force a reflow on each keystroke.
    React.useLayoutEffect(() => {
      if (autoSize) resizeTextArea(autoSizeRef.current)
    }, [autoSize, props.value, props.defaultValue])

    // The caller (a form's Controller, usually) owns the ref too, so both get it.
    const setRef = (element: HTMLTextAreaElement | null) => {
      if (autoSize) autoSizeRef.current = element
      if (typeof ref === "function") ref(element)
      else if (ref) ref.current = element
    }

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (autoSize) resizeTextArea(event.target)
      onChange?.(event)
    }

    return (
      <div className="w-full">
        <TextareaBase
          ref={setRef}
          className={cn(
            color && colorClasses[color],
            shadow && "shadow-sm",
            // overflow-y-auto, not hidden: the field grows only up to the max-h
            // its caller sets, and hiding the overflow past that point cut the
            // text off with no way to reach it.
            autoSize && "overflow-y-auto",
            className,
          )}
          style={autoSize ? { ...style, ...FIELD_SIZING_STYLE } : style}
          onChange={handleChange}
          {...props}
        />
        {helperText && (
          <p className={cn("mt-1 text-sm", color === "failure" ? "text-red-600" : "text-gray-500")}>{helperText}</p>
        )}
      </div>
    )
  },
)

CTextareaInner.displayName = "CTextareaInner"

// Main component that handles both uncontrolled and control patterns
export const CTextarea = React.forwardRef<HTMLTextAreaElement, CTextareaProps>((props, ref) => {
  // Check if using control pattern
  if ("control" in props && props.control) {
    const { control, name, rules, ...rest } = props

    return (
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field }) => {
          const fieldValue = typeof field.value === "string" ? field.value : ""
          return (
            <CTextareaInner
              ref={field.ref}
              name={field.name}
              value={fieldValue}
              onChange={field.onChange}
              onBlur={field.onBlur}
              {...rest}
            />
          )
        }}
      />
    )
  }

  // Using uncontrolled pattern
  return (
    <CTextareaInner
      ref={ref}
      {...props}
    />
  )
}) as <T extends FieldValues = FieldValues>(
  props: CTextareaProps<T> & { ref?: React.Ref<HTMLTextAreaElement> },
) => React.ReactElement
;(CTextarea as React.FC).displayName = "CTextarea"

// Export base textarea for reuse
export { TextareaBase }
